import React from "react";
import Header from "../header/Header";
import Footer from "../footer/Footer";
import MainPage from "../main-page/MainPage";
import { getTheme } from "../../services/LandingPage.Service";
import { getCurrentUser } from "../../services/MainPage.Service";
import { navigateReplace } from "../../services/Navigation.Service";
import Loader from "../loader/Loader";

const LandingPage = () => {
    const [isLoading, setIsLoading] = React.useState(true);
    const [themeData, setThemeData] = React.useState(null);
    const [lenderAction, setLenderAction] = React.useState('');
    const [currentUser, setCurrentUser] = React.useState(null);
    const [currentUserLoaded, setCurrentUserLoaded] = React.useState(false);
    // PATU-83: PATU-78 redirects a rejected OIDC sign-in to /{tpa}?authError=1
    // instead of a raw 500. showAuthError drives the (dismissible) banner;
    // suppressChallengeRef permanently disables the PATU-67 auto-challenge for
    // this page view so a just-failed sign-in is not bounced straight back into
    // the failing challenge (which would loop on a persistent misconfiguration).
    const [showAuthError, setShowAuthError] = React.useState(false);
    const suppressChallengeRef = React.useRef(false);

    const getData = async () => {
        const id = 1;
        await getTheme(id).then((res) => {
            if (!res) return;
            if (res.data) {
                localStorage.setItem('tpa_data', JSON.stringify(res.data[0]));
                setThemeData(res.data[0]);
            } else {
                setThemeData(res[0])
            }
            setIsLoading(false);
        });
    }

    React.useEffect(() => {
        setIsLoading(true);
        getData();
    }, [])

    React.useEffect(() => {
        let cancelled = false;
        getCurrentUser().then((me) => {
            if (!cancelled) {
                setCurrentUser(me);
                setCurrentUserLoaded(true);
            }
        });
        return () => { cancelled = true; };
    }, [])

    // PATU-83: detect the OIDC-rejection flag (set by PATU-78's OnRemoteFailure
    // redirect), surface a graceful message, and strip it from the URL so a
    // manual refresh starts a clean sign-in attempt rather than re-showing it.
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('authError') === '1') {
            suppressChallengeRef.current = true;
            setShowAuthError(true);
            params.delete('authError');
            const query = params.toString();
            window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));
        }
    }, [])

    // PATU-67: redirect to the per-tenant OIDC challenge when the tenant has
    // IdpConfig.Enabled and the caller is anonymous. Gated on BOTH themeData
    // and /api/me resolving — redirecting before /api/me returns would loop
    // (cookie valid → callback → SPA reloads → /api/me still pending → redirect).
    React.useEffect(() => {
        if (!themeData || !currentUserLoaded) return;
        // PATU-83: a sign-in that just failed lands here unauthenticated with
        // authError=1. Do not auto-challenge it — let the user read the message
        // and retry deliberately (otherwise a persistent rejection loops).
        if (suppressChallengeRef.current) return;
        if (!themeData?.idpConfig?.enabled) return;
        if (currentUser?.authenticated) return;
        const tpa = themeData.id;
        if (!tpa) return;
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        navigateReplace(`/signin/${tpa}?returnUrl=${returnUrl}`);
    }, [themeData, currentUserLoaded, currentUser])

    const handleLenderActions = (lenderDetails) => {
        setLenderAction(lenderDetails)
    }

    // PATU-67 follow-up: when the redirect effect above is going to fire, keep
    // the loader up instead of painting the portal UI behind it. The full app
    // would otherwise flash for a frame (theme resolves -> isLoading clears ->
    // UI renders -> the post-commit redirect effect runs). currentUser is null
    // while /api/me is still pending, so !currentUser?.authenticated also holds
    // the loader during that window — no separate currentUserLoaded gate needed.
    // authError (suppressChallengeRef) intentionally falls through so the graceful
    // error banner renders rather than a stuck spinner.
    const willChallenge =
        !!themeData?.idpConfig?.enabled &&
        !suppressChallengeRef.current &&
        !currentUser?.authenticated;

    return (
        <div>
            {(isLoading || willChallenge) ? <Loader isLoading={isLoading} /> :
                <div>
                    <Header handleLenderActions={handleLenderActions} isContractLookup={true} currentUser={currentUser} />
                    {showAuthError &&
                        <div
                            role="alert"
                            data-testid="auth-error-banner"
                            style={{ background: '#fdecea', color: '#611a15', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}
                        >
                            <span style={{ flex: 1 }}>Your sign-in could not be completed. Please try again.</span>
                            <button
                                type="button"
                                data-testid="auth-error-retry"
                                onClick={() => themeData?.id && navigateReplace(`/signin/${themeData.id}`)}
                            >
                                Try again
                            </button>
                            <button
                                type="button"
                                aria-label="Dismiss"
                                data-testid="auth-error-dismiss"
                                onClick={() => setShowAuthError(false)}
                            >
                                &times;
                            </button>
                        </div>
                    }
                    <MainPage mainPageData={themeData && themeData} lenderActionsDetails={lenderAction} currentUser={currentUser} />
                    <Footer />
                </div>
            }
        </div>
    )
}

export default LandingPage;
